/**
 * Invite a user to an organization by email (POST /users/inviteUser).
 * The backend mails the right link for new vs existing accounts.
 */

import { useState } from 'react';
import { useDataProvider, useGetList, useNotify, useTranslate } from 'react-admin';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { PersonAddAlt1Outlined as InviteIcon } from '@mui/icons-material';
import { actionButtonSx } from '@/components/common/ListPageHeader';
import type { CustomDataProvider } from '@/providers/dataProvider';

export const InviteUserButton = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [open, setOpen] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [busy, setBusy] = useState(false);

  const { data: organizations } = useGetList('organizations', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const result = await dataProvider.inviteUser({
        full_name: inviteName.trim(),
        email: inviteEmail.trim().toLowerCase(),
        organization_id: Number(organizationId),
        type: 'staff',
        role_id: 3,
      });
      if (result.status === 'error') {
        notify(result.message || 'ra.notification.http_error', { type: 'error' });
      } else {
        notify('resources.staff.invite.success', { type: 'info' });
        setOpen(false);
        setInviteName('');
        setInviteEmail('');
        setOrganizationId('');
      }
    } catch (error: any) {
      notify(error?.message || 'ra.notification.http_error', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        sx={actionButtonSx}
        startIcon={<InviteIcon />}
        onClick={() => setOpen(true)}
      >
        {translate('resources.staff.invite.button')}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{translate('resources.staff.invite.title')}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            sx={{ mt: 1 }}
            label={translate('resources.staff.fields.full_name')}
            value={inviteName}
            onChange={(event) => setInviteName(event.target.value)}
          />
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            type="email"
            label={translate('resources.staff.fields.email')}
            value={inviteEmail}
            onChange={(event) => setInviteEmail(event.target.value)}
          />
          <TextField
            select
            fullWidth
            sx={{ mt: 2 }}
            label={translate('resources.staff.fields.organization_id')}
            value={organizationId}
            onChange={(event) => setOrganizationId(event.target.value)}
          >
            {(organizations ?? []).map((org: any) => (
              <MenuItem key={org.user_id} value={org.user_id}>
                {org.company_name || org.full_name}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{translate('ra.action.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={!inviteName || !inviteEmail || !organizationId || busy}
          >
            {translate('resources.staff.invite.send')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InviteUserButton;
